import { useEffect, useState } from "react";
import {
  Table,
  Tag,
  Button,
  Popconfirm,
  message,
  notification,
  Empty,
  Spin,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  MdComputer,
  MdPhoneAndroid,
  MdTablet,
  MdDeviceUnknown,
} from "react-icons/md";
import { RiLogoutCircleLine } from "react-icons/ri";
import PageHeader from "../../component/pageHeaders";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { logout } from "../../redux/slices/userslice";
import api from "../../config/axiosInstance";
import moment from "moment";

interface DeviceSession {
  id: number;
  device_id: string;
  user_agent: string;
  ip_address: string;
  is_active: boolean;
  last_active_at: string;
  createdAt: string;
}

function getDeviceInfo(userAgent: string) {
  const ua = (userAgent || "").toLowerCase();
  if (ua.includes("mobile") || ua.includes("android") || ua.includes("iphone"))
    return { type: "mobile", icon: <MdPhoneAndroid size={24} color="#0055a6" /> };
  if (ua.includes("tablet") || ua.includes("ipad"))
    return { type: "tablet", icon: <MdTablet size={24} color="#0055a6" /> };
  if (
    ua.includes("windows") ||
    ua.includes("macintosh") ||
    ua.includes("linux")
  )
    return { type: "desktop", icon: <MdComputer size={24} color="#0055a6" /> };
  return { type: "unknown", icon: <MdDeviceUnknown size={24} color="#888" /> };
}

function getBrowserName(userAgent: string) {
  const ua = (userAgent || "").toLowerCase();
  if (ua.includes("edg/")) return "Edge";
  if (ua.includes("chrome") && !ua.includes("edg")) return "Chrome";
  if (ua.includes("firefox")) return "Firefox";
  if (ua.includes("safari") && !ua.includes("chrome")) return "Safari";
  if (ua.includes("opera") || ua.includes("opr")) return "Opera";
  return "Unknown Browser";
}

function getOSName(userAgent: string) {
  const ua = (userAgent || "").toLowerCase();
  if (ua.includes("windows")) return "Windows";
  if (ua.includes("macintosh") || ua.includes("mac os")) return "macOS";
  if (ua.includes("linux") && !ua.includes("android")) return "Linux";
  if (ua.includes("android")) return "Android";
  if (ua.includes("iphone") || ua.includes("ipad")) return "iOS";
  return "Unknown OS";
}

function SessionsScreen() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [sessions, setSessions] = useState<DeviceSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [revokingId, setRevokingId] = useState<number | null>(null);
  const [loggingOutAll, setLoggingOutAll] = useState(false);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const response = await api.get("auth/sessions");
      setSessions(response.data?.sessions || []);
    } catch (error) {
      message.error("Failed to load sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const revokeSession = async (sessionId: number) => {
    setRevokingId(sessionId);
    try {
      await api.delete(`auth/sessions/${sessionId}`);
      notification.success({
        message: "Session Revoked",
        description: "The device session has been terminated.",
      });
      fetchSessions();
    } catch (error) {
      message.error("Failed to revoke session");
    } finally {
      setRevokingId(null);
    }
  };

  const logoutAllDevices = async () => {
    setLoggingOutAll(true);
    try {
      await api.post("auth/logout-all");
      notification.success({
        message: "All Sessions Revoked",
        description: "You have been logged out from all devices.",
      });
      dispatch(logout({}));
      navigate("/");
    } catch (error) {
      message.error("Failed to logout from all devices");
      setLoggingOutAll(false);
    }
  };

  const columns: ColumnsType<DeviceSession> = [
    {
      title: "Device",
      key: "device",
      width: 300,
      render: (_, record) => {
        const device = getDeviceInfo(record.user_agent);
        const browser = getBrowserName(record.user_agent);
        const os = getOSName(record.user_agent);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 8,
                background: "#f0f5ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {device.icon}
            </div>
            <div>
              <div
                style={{
                  fontFamily: "Montserrat-SemiBold",
                  fontSize: 13,
                }}
              >
                {browser} on {os}
              </div>
              <div style={{ fontSize: 11, color: "#888" }}>
                {device.type.charAt(0).toUpperCase() + device.type.slice(1)}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: "IP Address",
      dataIndex: "ip_address",
      key: "ip_address",
      width: 160,
      render: (ip: string) => (
        <Tag style={{ fontFamily: "monospace" }}>{ip || "Unknown"}</Tag>
      ),
    },
    {
      title: "Last Active",
      dataIndex: "last_active_at",
      key: "last_active_at",
      width: 200,
      render: (date: string) => (
        <div>
          <div style={{ fontSize: 13 }}>{moment(date).fromNow()}</div>
          <div style={{ fontSize: 11, color: "#888" }}>
            {moment(date).format("DD MMM YYYY, hh:mm A")}
          </div>
        </div>
      ),
    },
    {
      title: "Login Time",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 200,
      render: (date: string) => (
        <div style={{ fontSize: 12, color: "#555" }}>
          {moment(date).format("DD MMM YYYY, hh:mm A")}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_, record) => (
        <Popconfirm
          title="Revoke this session?"
          description="This device will be logged out immediately."
          okText="Revoke"
          cancelText="Cancel"
          onConfirm={() => revokeSession(record.id)}
        >
          <Button
            danger
            size="small"
            loading={revokingId === record.id}
            icon={<RiLogoutCircleLine />}
          >
            Revoke
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Active Sessions"
        loader={loading}
        length="Dashboard / Profile / Active Sessions"
      >
        <div style={{ display: "flex", gap: 10 }}>
          <Button onClick={fetchSessions} disabled={loading}>
            Refresh
          </Button>
          <Popconfirm
            title="Logout from all devices?"
            description="You will be logged out from every device, including this one."
            okText="Logout All"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
            onConfirm={logoutAllDevices}
          >
            <Button danger loading={loggingOutAll}>
              Logout All Devices
            </Button>
          </Popconfirm>
        </div>
      </PageHeader>
      <div style={{ padding: "10px 0" }}>
        <div
          style={{
            background: "#fff",
            borderRadius: 8,
            padding: 20,
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{
              fontFamily: "Montserrat-SemiBold",
              fontSize: 15,
              marginBottom: 4,
            }}
          >
            Devices where you are logged in
          </div>
          <div style={{ fontSize: 13, color: "#888", marginBottom: 16 }}>
            These are the devices that are currently logged in to your account.
            If you see a device you don't recognize, revoke it immediately.
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Spin size="large" />
            </div>
          ) : sessions.length === 0 ? (
            <Empty description="No active sessions found" />
          ) : (
            <Table
              columns={columns}
              dataSource={sessions}
              pagination={false}
              size="small"
              rowKey="id"
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default SessionsScreen;
